'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { listContent, searchAll, type SortKey } from '@/lib/api'
import type { ContentItem } from '@/lib/types'
import { MovieCard } from '@/components/ui/MovieCard'
import { GridSkeleton } from '@/components/ui/Skeletons'
import { EmptyState, ErrorState } from '@/components/ui/States'
import { ChipGroup } from '@/components/ui/ChipGroup'
import {
  CONTENT_TYPE_OPTIONS,
  GENRES,
  RATING_OPTIONS,
  SORT_OPTIONS,
  YEAR_OPTIONS,
} from '@/lib/constants'
import { cn } from '@/lib/utils'

type TypeFilter = 'movie' | 'series' | 'all'

const PAGE = 50

export function KesfetClient() {
  const params = useSearchParams()

  const [type, setType] = useState<TypeFilter>((params.get('type') as TypeFilter) || 'movie')
  const [genre, setGenre] = useState<string | null>(params.get('genre'))
  const [sort, setSort] = useState<SortKey>((params.get('sort') as SortKey) || 'popular')
  const [minRating, setMinRating] = useState<number>(0)
  const [yearFrom, setYearFrom] = useState<number>(0)
  const [query, setQuery] = useState('')
  const [committedQuery, setCommittedQuery] = useState('')

  const [items, setItems] = useState<ContentItem[]>([])
  const [skip, setSkip] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [showFilters, setShowFilters] = useState(false)

  const reqId = useRef(0)

  const fetchCatalog = useCallback(
    async (skipVal: number): Promise<ContentItem[]> => {
      if (type === 'all') {
        const [m, s] = await Promise.all([
          listContent({ type: 'movie', genre, sort, minRating, yearFrom, skip: skipVal }),
          listContent({ type: 'series', genre, sort, minRating, yearFrom, skip: skipVal }),
        ])
        // interleave
        const out: ContentItem[] = []
        const max = Math.max(m.length, s.length)
        for (let i = 0; i < max; i++) {
          if (m[i]) out.push(m[i])
          if (s[i]) out.push(s[i])
        }
        return out
      }
      return listContent({ type, genre, sort, minRating, yearFrom, skip: skipVal })
    },
    [type, genre, sort, minRating, yearFrom]
  )

  // Reset + load when filters or committed query change
  useEffect(() => {
    const id = ++reqId.current
    setLoading(true)
    setError(false)
    setItems([])
    setSkip(0)
    setHasMore(true)

    const run = async () => {
      try {
        if (committedQuery) {
          const res = await searchAll(committedQuery)
          let merged =
            type === 'movie' ? res.movies : type === 'series' ? res.series : [...res.movies, ...res.series]
          if (genre) merged = merged.filter((i) => i.genres.includes(genre))
          if (minRating) merged = merged.filter((i) => (i.rating || 0) >= minRating)
          if (yearFrom) merged = merged.filter((i) => (i.year || 0) >= yearFrom)
          if (id === reqId.current) {
            setItems(merged)
            setHasMore(false)
          }
        } else {
          const res = await fetchCatalog(0)
          if (id === reqId.current) {
            setItems(res)
            setHasMore(res.length >= PAGE * (type === 'all' ? 1.5 : 1) || res.length >= 40)
          }
        }
      } catch (e) {
        console.error('[kesfet]', e)
        if (id === reqId.current) setError(true)
      } finally {
        if (id === reqId.current) setLoading(false)
      }
    }
    run()
  }, [type, genre, sort, minRating, yearFrom, committedQuery, fetchCatalog])

  const loadMore = async () => {
    if (loadingMore || committedQuery) return
    setLoadingMore(true)
    const nextSkip = skip + PAGE
    try {
      const res = await fetchCatalog(nextSkip)
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id))
        const fresh = res.filter((r) => !seen.has(r.id))
        return [...prev, ...fresh]
      })
      setSkip(nextSkip)
      setHasMore(res.length >= 40)
    } catch (e) {
      console.error('[kesfet] loadMore', e)
    } finally {
      setLoadingMore(false)
    }
  }

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCommittedQuery(query.trim())
  }

  const clearSearch = () => {
    setQuery('')
    setCommittedQuery('')
  }

  return (
    <div className="container-page py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          KENDİN SEÇ
        </h1>
        <p className="mt-2 text-neutral-400">Aradığın filmi veya diziyi bul.</p>
      </div>

      {/* Search */}
      <form onSubmit={submitSearch} className="mb-4 flex gap-2">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500">🔍</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Film veya dizi ara..."
            className="w-full rounded-full border border-white/15 bg-white/5 py-3 pl-11 pr-4 text-sm text-white placeholder:text-neutral-500 focus:border-crimson-500 focus:outline-none"
          />
        </div>
        <button type="submit" className="btn-primary">Ara</button>
        {committedQuery && (
          <button type="button" onClick={clearSearch} className="btn-ghost">
            Temizle
          </button>
        )}
      </form>

      {/* Filter toggle (mobile) */}
      <button
        onClick={() => setShowFilters((v) => !v)}
        className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-neutral-200 md:hidden"
      >
        <span>⚙️</span> Filtreler {showFilters ? '▲' : '▼'}
      </button>

      {/* Filters */}
      <div className={cn('surface mb-8 space-y-5 p-5', !showFilters && 'hidden md:block')}>
        <ChipGroup
          label="İçerik"
          options={CONTENT_TYPE_OPTIONS}
          value={type}
          onChange={(v) => setType(v as TypeFilter)}
        />
        <ChipGroup
          label="Tür"
          options={[{ value: '', label: 'Hepsi' }, ...GENRES.map((g) => ({ value: g, label: g }))]}
          value={genre ?? ''}
          onChange={(v) => setGenre(v || null)}
        />
        <div className="grid gap-5 sm:grid-cols-3">
          <ChipGroup
            label="Sırala"
            options={SORT_OPTIONS}
            value={sort}
            onChange={(v) => setSort(v as SortKey)}
          />
          <ChipGroup
            label="Puan"
            options={RATING_OPTIONS}
            value={String(minRating)}
            onChange={(v) => setMinRating(Number(v))}
          />
          <ChipGroup
            label="Yıl"
            options={YEAR_OPTIONS}
            value={String(yearFrom)}
            onChange={(v) => setYearFrom(Number(v))}
          />
        </div>
      </div>

      {/* Results */}
      {error ? (
        <ErrorState onRetry={() => setCommittedQuery((q) => q)} />
      ) : loading ? (
        <GridSkeleton count={18} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Sonuç bulunamadı"
          message={
            committedQuery
              ? `"${committedQuery}" için sonuç bulunamadı. Farklı bir arama dene.`
              : 'Aradığın kriterlere uygun bir sonuç bulamadık.'
          }
        />
      ) : (
        <>
          <p className="mb-4 text-sm text-neutral-500">{items.length} sonuç gösteriliyor</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {items.map((item, i) => (
              <MovieCard key={`${item.type}-${item.id}`} item={item} eager={i < 6} />
            ))}
          </div>

          {hasMore && !committedQuery && (
            <div className="mt-10 flex justify-center">
              <button onClick={loadMore} disabled={loadingMore} className="btn-primary min-w-48">
                {loadingMore ? 'Yükleniyor...' : 'Daha Fazla Yükle'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
